/**
 * FINDINGS-DOMAIN-1 Session 4 / SCOPE-CONFIG-1 — scope, quote and tier config.
 *
 * Every count and every dollar figure on these screens comes back from the
 * server. Nothing in this file (or in the pages that use it) hard-codes 17, 54,
 * 74 or a fee coefficient: the catalogue is the source of truth, and a screen
 * that re-typed its numbers would drift from the report the customer receives.
 *
 * Lives outside client.ts because that file is already at the FILE_SIZE_POLICY
 * split-first band.
 */
import { apiClient } from '@/api/client'
import { money } from '@/utils/format'

export interface CatalogueFinding {
  number: number
  title: string
  section: string
  methodology: string
  procedure: string
  tiers: string[]
}

export interface CatalogueSection {
  letter: string
  label: string
  finding_count: number
  tier_counts: Record<string, number>
  findings: CatalogueFinding[]
}

export interface TierInfo {
  key: string
  label: string
  order: number
  finding_count: number
  recommended_ebitda_min: number | null
  recommended_ebitda_max: number | null
  fee_base: number | null
  fee_per_million: number | null
  fee_formula: string | null
  pricing_source: string
  turnaround?: string | null
}

export interface CatalogueResponse {
  catalogue_count: number
  section_count: number
  sections: CatalogueSection[]
  tiers: TierInfo[]
}

export interface QuoteRequest {
  ebitda: number
  tier?: string
  deal_side?: string
  entity_count?: number
  industry?: string
  tax_diligence?: boolean
  discount_pct?: number
  discount_reason?: string
}

export interface QuoteSection {
  letter: string
  label: string
  in_scope: number
  finding_count: number
}

export interface QuoteResponse {
  recommended_tier: string
  tier: string
  tier_label: string
  ebitda: number
  anchor_fee: number
  base_fee: number
  discount_pct: number
  discount_reason: string | null
  turnaround: string
  pricing_source: string
  in_scope_count: number
  catalogue_count: number
  section_count: number
  sections: QuoteSection[]
  scope_facts: Record<string, unknown>
  tiers: TierInfo[]
}

export interface ScopeConfig {
  membership: Record<string, { added?: number[]; removed?: number[] }>
  pricing: Record<string, { base?: number; per_million?: number }>
}

export interface ScopeConfigResponse {
  config: ScopeConfig
  counts: Record<string, number>
  baseline_counts: Record<string, number>
  catalogue_count: number
  pricing: Record<string, { base: number; per_million: number; source: string }>
  baseline_pricing: Record<string, { base: number; per_million: number; formula: string }>
  tiers: Array<{ key: string; label: string; order: number; numbers: number[] }>
  saved?: boolean
}

export interface ScopeDeltaResponse {
  deal_id: string
  scope_tier: string
  scope_delta: { added: number[]; removed: number[]; reason: string } | null
  in_scope_count: number
  in_scope_numbers: number[]
  added: Array<{ number: number; title: string; section: string }>
  removed: Array<{ number: number; title: string; section: string }>
  reason: string | null
  disclosure: string[]
}

export async function fetchCatalogue(): Promise<CatalogueResponse> {
  const { data } = await apiClient.get<CatalogueResponse>('/qoe/catalogue')
  return data
}

export async function buildQuote(body: QuoteRequest): Promise<QuoteResponse> {
  const { data } = await apiClient.post<QuoteResponse>('/qoe/quote', body)
  return data
}

export async function fetchScopeConfig(): Promise<ScopeConfigResponse> {
  const { data } = await apiClient.get<ScopeConfigResponse>('/qoe/admin/scope-config')
  return data
}

export async function previewScopeConfig(config: ScopeConfig): Promise<ScopeConfigResponse> {
  const { data } = await apiClient.post<ScopeConfigResponse>(
    '/qoe/admin/scope-config/preview',
    config,
  )
  return data
}

/**
 * Save the overlay. `expectedCounts` is the confirmation the server demands
 * before it will apply a membership change — a 409 comes back with the real
 * counts when it does not match, and the caller is expected to show them.
 */
export async function saveScopeConfig(
  config: ScopeConfig,
  expectedCounts: Record<string, number>,
  reason?: string,
): Promise<ScopeConfigResponse> {
  const { data } = await apiClient.put<ScopeConfigResponse>('/qoe/admin/scope-config', {
    ...config,
    expected_counts: expectedCounts,
    reason,
  })
  return data
}

export async function fetchScopeDelta(dealId: string): Promise<ScopeDeltaResponse> {
  const { data } = await apiClient.get<ScopeDeltaResponse>(`/qoe/deals/${dealId}/scope-delta`)
  return data
}

export async function saveScopeDelta(
  dealId: string,
  body: { added: number[]; removed: number[]; reason: string },
): Promise<ScopeDeltaResponse> {
  const { data } = await apiClient.put<ScopeDeltaResponse>(
    `/qoe/deals/${dealId}/scope-delta`,
    body,
  )
  return data
}

export async function clearScopeDelta(dealId: string): Promise<ScopeDeltaResponse> {
  const { data } = await apiClient.delete<ScopeDeltaResponse>(`/qoe/deals/${dealId}/scope-delta`)
  return data
}

/** `$1.0M` / `$750K` / `—`. Bands and fees are read at a glance, not audited. */
export function fmtMoney(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `$${Math.round(value / 1_000)}K`
  return `$${value.toLocaleString()}`
}

/** Exact dollars, for the fee a customer is being asked to agree to. */
export function fmtFee(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return money(value)
}

export function fmtBand(tier: TierInfo): string {
  const { recommended_ebitda_min: lo, recommended_ebitda_max: hi } = tier
  if (lo === null && hi === null) return 'Any EBITDA'
  if (lo === null) return `Up to ${fmtMoney(hi)} EBITDA`
  if (hi === null) return `Over ${fmtMoney(lo)} EBITDA`
  return `${fmtMoney(lo)}–${fmtMoney(hi)} EBITDA`
}
